












































































































































































import {
  Vue,
  Component,
  Prop
} from "@/shared/vuepack";
import {
  FetchState,
  FundingOption
} from "@/shared/types";
import {
  Contact,
  PayrollInformation
} from "@/employer/types";
import { Validator } from "vee-validate";
import * as RouteNames from "@/employer/route-names";
import * as _ from "lodash";
import moment from "moment";
import Input from "@/shared/components/ui/Input.vue";
import RadioButton from "@/shared/components/ui/RadioButton.vue";
import Checkbox from "@/shared/components/ui/Checkbox.vue";
import Button from "@/shared/components/ui/Button.vue";
import ErrorSummary from "@/shared/components/ErrorSummary.vue";
import EmployerPhone from "@/employer/components/shared/EmployerPhone.vue";
import { useNotifierStore } from "@/shared/store/notifier";
import { useProfileStore } from "@/employer/store/profile";
import { useUserStore } from "@/shared/store/user";
import { watch } from "@vue/composition-api";

const PayCycles = {
  none: "none",
  weekly: "weekly",
  biWeekly: "biWeekly",
  semiMonthly: "semiMonthly",
  monthly: "monthly"
};

@Component({
  components: {
    Input,
    RadioButton,
    Checkbox,
    Button,
    EmployerPhone,
    ErrorSummary
  }
})
export default class Payroll extends Vue {
  $validator: Validator;

  notifierStore = useNotifierStore();
  profileStore = useProfileStore();
  userStore = useUserStore();

  get employerId () {
    return this.forEmployerId || this.userStore.employerId;
  }

  get isSelfSignup () {
    return !this.forEmployerId || this.forEmployerId === this.userStore.employerId;
  }

  @Prop({ required: false })
  forEmployerId: number;

  @Prop()
  submitButtonText: string;

  @Prop()
  greyBackground: boolean;

  @Prop()
  fetchEnrollmentData: false;

  get submitText (): string {
    return this.submitButtonText ? this.submitButtonText : "Next";
  }

  get arePayrollFieldsDisabled (): boolean {
    return this.showSaveNextButton === false;
  }

  get showSaveNextButton (): boolean {
    if (this.userStore.currentEmployer && this.userStore.currentEmployer.employerId === this.employerId) {
      return this.userStore.currentEmployer.fundingOption
          && this.userStore.currentEmployer.fundingOption === FundingOption.None;
    }
    return true;
  }

  get tooltipCutoffDays (): string {
    return `<p>The CutOff days are the number of days (typically 3-5) needed to process
      contribution updates before your payroll system's deadline.</p>`;
  }

  providers = [
    "ADP Run",
    "ADP Vantage",
    "ADP Workforce Now",
    "Bamboo",
    "Ceridian",
    "Gusto",
    "Intuit (Quickbooks)",
    "iSolved",
    "Kronos Workforce Ready",
    "Namely",
    "PayCom",
    "Paylocity",
    "PayChex",
    "Workday",
    "Zenefits",
  ];

  isSubmitting = false;
  formModel: PayrollInformation = null;
  otherUserIsPayrollContact = false;
  payrollContactReadOnly = false;
  originalContact: Contact;
  otherProviderOption = "Other";
  payrollProviderName = "";
  otherPayrollProviderName = "";

  initializeFromStateValue () {
    if (this.profileStore.payrollInformationState === FetchState.Loaded) {
      const stateVersion = _.cloneDeep(this.profileStore.payrollInformation);
      if (!stateVersion.contact) {
        stateVersion.contact = { name: null, email: null, phone: null, userId: null };
      }

      const userIsPayrollContact = stateVersion.contact.userId === this.userStore.userId;
      this.originalContact = { ...(stateVersion.contact) };
      this.otherUserIsPayrollContact = !userIsPayrollContact && !!stateVersion.contact.userId;
      this.payrollContactReadOnly = this.otherUserIsPayrollContact;
      if (this.isStatePayrollProviderInSelectList()) {
        this.payrollProviderName = stateVersion.payrollProviderName;
        this.otherPayrollProviderName = "";
      } else {
        this.payrollProviderName = this.otherProviderOption;
        this.otherPayrollProviderName = stateVersion.payrollProviderName;
      }
      if (stateVersion.payrollCycle === PayCycles.none) {
        // empty string will trigger validation for required field
        stateVersion.payrollCycle = "";
      }
      stateVersion.fundingOption = this.profileStore.fundingOption;

      this.formModel = stateVersion;
    }
  }

  isStatePayrollProviderInSelectList (): boolean {
    const providerName = this.profileStore.payrollInformation.payrollProviderName;
    return (providerName.length === 0 || this.providers.some(name => name === providerName));
  }

  created (): void {
    watch(() => this.profileStore.payrollInformationState,
        () => {
          this.initializeFromStateValue();
        },
        { deep: true });

    this.initializeFromStateValue();

    this.profileStore.getPayrollInformation(this.employerId)
        .catch(this.handleError);

    if (this.fetchEnrollmentData) {
      this.profileStore.loadEnrollmentMetrics(this.employerId)
          .catch(this.handleError);
    }
  }

  togglePayrollContactEdit () {
    this.payrollContactReadOnly = !this.payrollContactReadOnly;
    if (this.payrollContactReadOnly) {
      this.formModel.contact = { ...this.originalContact };
    } else {
      this.formModel.contact = { email: null, name: null, userId: null, phone: null };
    }
  }

  get isLoaded (): boolean {
    return this.profileStore.payrollInformationState === FetchState.Loaded;
  }

  get showDayOfWeek (): boolean {
    return this.formModel.payrollCycle === PayCycles.weekly || this.formModel.payrollCycle === PayCycles.biWeekly;
  }

  get showOtherProviderName () {
    return this.payrollProviderName === this.otherProviderOption;
  }

  vueRequiredIf (test: boolean) {
    return test ? "required" : false;
  }

  get validateDayOfWeek () {
    return this.vueRequiredIf(this.showDayOfWeek);
  }

  get validateOtherProviderName () {
    return this.payrollProviderName === this.otherProviderOption ? "required" : "";
  }

  get showSemiMonthly () {
    return this.formModel.payrollCycle === PayCycles.semiMonthly;
  }

  get validateSemiMonthly () {
    return this.vueRequiredIf(this.showSemiMonthly);
  }

  get showDayOfMonth () {
    return this.formModel.payrollCycle === PayCycles.monthly;
  }

  get validateDayOfMonth () {
    return this.vueRequiredIf(this.showDayOfMonth);
  }

  get payrollFirstDayOfYear (): string {
    const firstDayOfYear = this.formModel.firstDateOfYear;
    if (!firstDayOfYear) {
      return null;
    }
    return moment(firstDayOfYear).format("YYYY-MM-DD");
  }

  set payrollFirstDayOfYear (date) {
    this.formModel.firstDateOfYear = date;
  }

  get nextPayDate () {
    return moment(this.formModel.nextPayrollDate).format("MM/DD/YYYY");
  }

  get nextPayCutOff () {
    return moment(this.formModel.nextPayrollCutOffDate).subtract(1, "d").format("MM/DD/YYYY");
  }

  get showPayDateMessage () {
    return moment(this.formModel.nextPayrollDate).isValid();
  }

  get dayOfWeekSelection () {
    return this.formModel.dayOfWeek || "";
  }

  set dayOfWeekSelection (val: string) {
    this.formModel.dayOfWeek = val ? val : null;
  }

  get validatePayrollContactName () {
    return this.vueRequiredIf(this.showPayrollContactFields);
  }

  get validatePayrollContactPhone () {
    return this.showPayrollContactFields ? "required|phoneWithExtension" : false;
  }

  get validatePayrollContactEmail () {
    return this.showPayrollContactFields ? "required|email" : false;
  }

  get currentUserIsContact () {
    return this.formModel.contact && this.formModel.contact.userId === this.userStore.userId;
  }

  set currentUserIsContact (checkValue: boolean) {
    this.formModel.contact.userId = (checkValue) ? this.userStore.userId : null;
    if (!checkValue) {
      this.formModel.contact.userId = null;
      this.formModel.contact.name = null;
      this.formModel.contact.email = null;
      this.formModel.contact.phone = null;
    } else {
      this.formModel.contact.userId = this.userStore.userId;
      this.formModel.contact.name = this.userStore.displayName;
      this.formModel.contact.email = this.userStore.email;
      this.formModel.contact.phone = this.userStore.phone;
    }
  }

  get showPayrollContactFields () {
    return !(this.currentUserIsContact || this.payrollContactReadOnly);
  }

  get suppressNotifierMessages (): boolean {
    // suppress notification from the notifier store for users in the onboarding flow

    return (this.$router.currentRoute.name === RouteNames.Onboarding ||
        this.$router.currentRoute.name === RouteNames.PartnerNewEmployer);
  }

  onSubmit (formModel: PayrollInformation): void {
    if (this.showOtherProviderName) {
      this.formModel.payrollProviderName = this.otherPayrollProviderName;
    } else {
      this.formModel.payrollProviderName = this.payrollProviderName;
    }
    this.$validator.validateAll()
        .then(result => {
          if (!result) {
            return;
          }

          this.isSubmitting = true;
          if (!this.arePayrollFieldsDisabled) {
            this.profileStore.savePayrollInformation({
              request: formModel,
              suppressNotifierMessages: this.suppressNotifierMessages
            }).then(resp => {
                  this.isSubmitting = false;
                  this.initializeFromStateValue();
                })
                .then(resp => this.$emit("complete"))
                .catch(this.handleError);
          } else {
            this.profileStore.savePayrollContactInformation({
              request: formModel,
              suppressNotifierMessages: this.suppressNotifierMessages
            }).then(resp => {
                  this.isSubmitting = false;
                  this.initializeFromStateValue();
                })
                .then(resp => this.$emit("complete"))
                .catch(this.handleError);
          }
        });
  }

  handleError (err: any): void {
    this.isSubmitting = false;
    this.notifierStore.setDanger(err &&
        err.response.data.errorMessage ||
        "Error processing your request. Please try again later");
  }

  get submitDisabled () {
    return this.$validator.errors.any() || this.isSubmitting;
  }
}
